import React, { useContext, useEffect, useState } from "react";
import "./style.css";
import {
  Button,
  Collapse,
  Modal,
  Input,
  Select,
  Form,
  message,
  Popconfirm,
  Radio,
  notification,
} from "antd";
import {
  alterUserStatus,
  createUser,
  editUser,
  getUsersList,
  loginAsUser,
  pageAccess,
  getCompanyList,
  deleteUser,
} from "../../../API/Admin/user";

import { ClipLoader } from "react-spinners";
import { UserContext } from "../../../App";
import { encryptText } from "../../../Utils/Encryption";
import updateUserStates from "../../../Utils/UpdateUsersState";
import { useNavigate } from "react-router-dom";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { checkApiKeyValidity } from "../../../API/auth";

const { Panel } = Collapse;
const { Option } = Select;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [companies, setcompanies] = useState([]);
  const {
    userData,
    setUserPages,
    setUserToken,
    setUserData,
    setUserApiKeyStatus,
  } = useContext(UserContext);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalVisibleEdit, setIsModalVisibleEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState();
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editParentCompany, setEditParentCompany] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [form2] = Form.useForm();

  const pages = [
    { id: 1, value: "dashboard", name: "Dashboard" },
    { id: 2, value: "mileage", name: "Mileage" },
    { id: 3, value: "fuel", name: "Fuel" },
    { id: 4, value: "idle", name: "Idle" },
    { id: 5, value: "fuel_efficiency", name: "Fuel Efficiency" },
    { id: 6, value: "sensor", name: "Sensor" },
    { id: 7, value: "servicing_reminder", name: "Servicing & Reminder" },
    { id: 8, value: "decision_engine", name: "Decision Engine" },
    { id: 9, value: "more_analysis", name: "More Analysis" },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getUsersList();
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  const fetchcompanies = async () => {
    try {
      setLoading(true);
      const response = await getCompanyList();
      setcompanies(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchcompanies();
  }, []);

  const handleCreateUser = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const response = await createUser(
        values?.username,
        values?.email,
        values?.role,
        values?.role === "user" ? values?.api_key : undefined,
        values?.address,
        values?.password,
        values?.parent_company
      );

      // Success
      if (response.data) {
        const newUser = response.data;
        setUsers((prevUsers) => [...prevUsers, newUser]);
        message.success("User created successfully!");
        setIsModalVisible(false);
        form.resetFields();
      }
    } catch (error) {
      notification.error({
        key: "Error",
        message: "Error",
        description: error?.response?.data?.api_key || "Can't Create User",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
      setIsModalVisible(false);
    }
  };

  const handleEdit = (user) => {
    setEditId(user?.id);
    setEditName(user?.name);
    setEditEmail(user?.email);
    setEditRole(user?.role);
    setEditParentCompany(user?.parent_company);
    setIsModalVisibleEdit(true);
  };

  const handleEditUser = async () => {
    try {
      const values = await form2.validateFields();
      setLoading(true);
      const response = await editUser(
        editId,
        values?.username,
        values?.email,
        values?.role,
        values?.api_key,
        values?.parent_company
      );

      if (response.data) {
        message.success(response?.data?.message);
        fetchData();
        form.resetFields();
      }
    } catch (error) {
      console.error("Error updating user:", error);
      notification.error({
        key: "Error",
        message: "Error",
        description: error?.response?.data?.api_key || "Can't Create User",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
      setIsModalVisibleEdit(false);
    }
  };

  const checkValidation = async () => {
    try {
      const response = await checkApiKeyValidity();
      setUserApiKeyStatus({
        status: response.data.status,
        message: response.data.message,
      });
    } catch (error) {
      message.error("Failed To Check Api Key Status");
    }
  };

  const handleLoginAsUser = async (id) => {
    try {
      setUserApiKeyStatus(null);
      message.success("Signing out from the admin account");
      const response = await loginAsUser(id);
      localStorage.clear();
      message.success("Logging into the user account");
      localStorage.setItem(
        encryptText("token"),
        encryptText(
          JSON.stringify({
            access: response?.data?.access,
            refresh: response?.data?.refresh,
          })
        )
      );
      localStorage.setItem(
        encryptText("user"),
        encryptText(
          JSON.stringify({
            id: response?.data?.id,
            role: response?.data?.role,
            verified: response?.data?.verified,
            name: response?.data?.name,
            email: response?.data?.email,
            profile_picture: response?.data?.profile_picture,
          })
        )
      );
      updateUserStates(setUserData, setUserToken);
      setUserPages(response?.data?.pages);
      if (response?.data?.verified) {
        if (response?.data?.role !== "admin") {
          await checkValidation();
        }
      } else {
        navigate("/verification-link");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  const handleChangeStatus = async (id, action) => {
    try {
      setLoading(true);
      const response = await alterUserStatus(id, action);
      message.success(response?.data?.message);
      setLoading(false);

      if (action === "enable") {
        setUsers((prevUsers) =>
          prevUsers?.map((user) =>
            user.id === id ? { ...user, status: "Active" } : user
          )
        );
      } else {
        setUsers((prevUsers) =>
          prevUsers?.map((user) =>
            user.id === id ? { ...user, status: "Inactive" } : user
          )
        );
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  const checkUserStatus = (status) => {
    if (status === "Active") {
      return true;
    } else {
      return false;
    }
  };

  const checkPageAccess = (page, pages) => {
    return pages?.includes(page);
  };

  const handleChangeAccess = async (id, page, action) => {
    try {
      const response = await pageAccess(id, page, action);
      message.success(response?.data?.message);

      setUsers((prevUsers) => {
        return prevUsers?.map((user) => {
          if (user.id === id) {
            let updatedPages;
            if (action === "view") {
              updatedPages = user?.pages?.includes(page)
                ? user.pages
                : [...user.pages, page];
            } else {
              updatedPages = user.pages.filter((p) => p !== page);
            }
            return { ...user, pages: updatedPages };
          }
          return user;
        });
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalVisibleEdit(false);
    setIsModalVisible(false);
    form.resetFields();
    form2.resetFields();
  };

  const handleVehicleModal = (id) => {
    navigate(`/vehicles/${id}`);
  };

  const handleDeleteUser = async (user_id) => {
    setLoadingDelete(true);

    try {
      await deleteUser(user_id);

      setUsers((prevUsers) =>
        prevUsers?.filter((user) => user?.id !== user_id)
      );

      message.success("User Deleted Successfully");
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleViewClick = (userId, key) => {
    if (activeKey === key) {
      setActiveKey(null);
    } else {
      setActiveKey(key);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        className="loader-container"
      >
        <ClipLoader size={50} color={"#123abc"} loading={loading} />
      </div>
    );
  }

  return (
    <div className="user-general">
      <div className="page-header">
        <div className="page-title">
          <div className="title">General</div>
          <div className="title-description">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>
        </div>

        <Button
          className="create-user-button"
          onClick={() => setIsModalVisible(true)}
        >
          Create new user
        </Button>
      </div>
      <Collapse
        bordered={false}
        activeKey={activeKey}
        expandIcon={() => null}
        accordion
        onChange={(key) => setActiveKey(key)}
      >
        {users?.map((user, index) => (
          <>
            <Panel
              header={
                <>
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="User-information"
                  >
                    <div className="Info-content">
                      <div className="profile-text">Team</div>

                      <div className="profile-info">
                        <div className="compact-avatar">
                          {user?.profile_picture ? (
                            <img
                              className="profile-pic"
                              src={user?.profile_picture}
                              alt="user-icon"
                            />
                          ) : (
                            <UserOutlined className="profile-pic" />
                          )}
                        </div>

                        <div className="profile-details">
                          <div className="profile-name">{user?.name}</div>
                          <div className="profile-email">{user?.email}</div>
                        </div>
                      </div>
                    </div>
                    <div className="profile-actions">
                      <div className="profile-line">
                        <div
                          onClick={() => {
                            handleEdit(user);
                          }}
                          className="edit-section"
                        >
                          <svg
                            width="1em"
                            height="1em"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7.50004 15.1667H14.5834M12.5 6.83333L15 4.33333L11.6667 1L9.16671 3.5M12.5 6.83333L4.16671 15.1667H0.833374V11.8333L9.16671 3.5M12.5 6.83333L9.16671 3.5"
                              stroke="#3B82F6"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          Edit
                        </div>
                        <div className="view-section">
                          <Button
                            icon={
                              activeKey === index.toString() ? (
                                <CaretUpOutlined />
                              ) : (
                                <CaretDownOutlined />
                              )
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewClick(user?.id, index.toString());
                            }}
                          >
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {activeKey === index.toString()
                                ? "Close"
                                : "View"}
                            </span>
                          </Button>
                        </div>
                      </div>
                      <div
                        className={`custom-status-btn ${
                          checkUserStatus(user?.status) ? "active" : "inactive"
                        }`}
                      >
                        {checkUserStatus(user?.status) ? "Active" : "Inactive"}
                      </div>
                    </div>
                  </div>
                </>
              }
              key={index}
              className="panel-style"
            >
              <div className="panel-content">
                <div className="section-user">
                  <div className="section-text-user">
                    <div className="section-title-user">Log in as user</div>
                    <div className="section-description-user">
                      Login as user to see their POV
                    </div>
                  </div>
                  <Button
                    name="action-button login-btn"
                    loading={loading}
                    onClick={() => {
                      handleLoginAsUser(user?.id);
                    }}
                    className="login-btn"
                  >
                    Login As User
                  </Button>
                </div>
                <div className="section-user">
                  <div className="section-text-user">
                    <div className="section-title-user">Account Status</div>
                    <div className="section-description-user">
                      You can enable or disable accounts of the user
                    </div>
                  </div>
                  <div className="button-group">
                    <Button
                      loading={loading}
                      className={`action-button ${
                        checkUserStatus(user?.status)
                          ? "disable-btn enable-selected"
                          : "enable-btn"
                      }`}
                      disabled={checkUserStatus(user?.status)}
                      onClick={() => handleChangeStatus(user?.id, "enable")}
                    >
                      Enable
                    </Button>
                    <Button
                      loading={loading}
                      className={`action-button ${
                        !checkUserStatus(user?.status)
                          ? "disable-btn disable-selected"
                          : "enable-btn"
                      }`}
                      disabled={!checkUserStatus(user?.status)}
                      onClick={() => handleChangeStatus(user?.id, "disable")}
                    >
                      Disable
                    </Button>
                  </div>
                </div>

                {!user.parent_company && (
                  <div className="section-user">
                    <div className="section-text-user">
                      <div className="section-title-user">
                        Vehicle Data Entry
                      </div>
                      <div className="section-description-user">
                        Enter details of your vehicles manually
                      </div>
                    </div>

                    <Button
                      className="enable-btn"
                      loading={loading}
                      type="primary"
                      onClick={() => handleVehicleModal(user?.id)}
                    >
                      Add Data
                    </Button>
                  </div>
                )}

                <div className="section-user page-access">
                  <div
                    className="section-title-user"
                    style={{ marginBottom: "15px" }}
                  >
                    Pages Access
                  </div>
                  {pages?.map((page, index) => (
                    <div className="section-options" key={page?.value || index}>
                      <div className="section-description">{page?.name}</div>
                      <div className="radio-group">
                        <Radio.Group
                          value={
                            checkPageAccess(page?.value, user?.pages)
                              ? "view"
                              : "restrict"
                          }
                          onChange={(e) =>
                            handleChangeAccess(
                              user?.id,
                              page.value,
                              e.target.value
                            )
                          }
                        >
                          <Radio value="view">View</Radio>
                          <Radio value="restrict">Restrict</Radio>
                        </Radio.Group>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="delete-panel">
                  <div className="section-user section-delete">
                    <div className="section-text-user">
                      <div className="section-title-user">
                        <div> Delete User</div>
                      </div>
                      <div
                        className="section-description-user"
                        style={{ color: "red" }}
                      >
                        This action will remove the user. Continue?
                      </div>
                    </div>
                    <Popconfirm
                      title="Delete the user"
                      description="Are you sure to delete this user?"
                      onConfirm={() => handleDeleteUser(user?.id)}
                      okText="Yes"
                      cancelText="No"
                      placement="left"
                    >
                      <Button
                        icon={<DeleteOutlined />}
                        loading={loadingDelete}
                        style={{ backgroundColor: "transparent" }}
                        className="delete-btn"
                        danger
                      >
                        Delete
                      </Button>
                    </Popconfirm>
                  </div>
                </div>
              </div>
            </Panel>
          </>
        ))}
      </Collapse>

      <Modal
        className="Edit-modal"
        title="Create New User"
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={handleModalClose}
        confirmLoading={loading}
        okText="Create"
        cancelText="Cancel"
        okButtonProps={{ className: "user-ok-button" }}
        cancelButtonProps={{ className: "user-cancel-button" }}
      >
        <Form
          layout="vertical"
          form={form}
          className="form"
          onFinish={handleCreateUser}
        >
          <Form.Item
            label="Username"
            name="username"
            rules={[{ required: true, message: "Please input the username!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                required: true,
                type: "email",
                message: "Please input a valid email!",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Company (optional)" name="parent_company">
            <Select
              suffixIcon={<CaretDownOutlined />}
              popupClassName="custom-select-dropdown"
              placeholder={
                selectedRole === "company"
                  ? "Parent company not needed for Company role"
                  : "Select a parent company (optional)"
              }
              disabled={selectedRole === "company"}
              allowClear
              onChange={(value) => setSelectedCompany(value)}
            >
              {companies?.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Address"
            name="address"
            rules={[
              {
                required: true,
                message: "Please input the company address!",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: "Please select the role!" }]}
          >
            <Select
              placeholder="Select a role"
              onChange={(value) => setSelectedRole(value)}
              popupClassName="custom-select-dropdown"
              suffixIcon={<CaretDownOutlined />}
            >
              <Option value="admin">Admin</Option>
              <Option value="user">User</Option>
            </Select>
          </Form.Item>

          {selectedRole === "user" && !selectedCompany && (
            <Form.Item
              label="API Key"
              name="api_key"
              rules={[{ required: true, message: "Please enter API key!" }]}
            >
              <Input />
            </Form.Item>
          )}

          <div className="password-field">
            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: "Please input the password!" },
                { min: 6, message: "Password must be at least 6 characters!" },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <Modal
        className="Edit-modal"
        form={form2}
        title="Edit User"
        visible={isModalVisibleEdit}
        onCancel={() => handleModalClose()}
        onOk={() => form2.submit()}
        confirmLoading={loading}
        okText="Save Changes"
        cancelText="Cancel"
      >
        <Form
          initialValues={{
            username: editName,
            email: editEmail,
            role: editRole,
            parent_company: editParentCompany,
          }}
          layout="vertical"
          onFinish={handleEditUser}
          className="form"
          form={form2}
        >
          <div className="item-wrapper">
            <Form.Item
              label="Username"
              name="username"
              rules={[
                { required: true, message: "Please input the username!" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Please input a valid email!",
                },
              ]}
            >
              <Input disabled />
            </Form.Item>
          </div>
          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: "Please select the role!" }]}
          >
            <Select
              suffixIcon={<CaretDownOutlined />}
              placeholder="Select a role"
              popupClassName="custom-select-dropdown"
            >
              <Option value="admin">Admin</Option>
              <Option value="user">User</Option>
            </Select>
          </Form.Item>
          <Form.Item
            shouldUpdate={(prev, curr) => prev.role !== curr.role}
            noStyle
          >
            {({ getFieldValue }) =>
              getFieldValue("role") === "user" ? (
                <Form.Item
                  label="API Key"
                  name="api_key"
                  rules={[{ required: true, message: "Please enter API Key!" }]}
                >
                  <Input placeholder="Enter API Key" />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item label="Parent Company" name="parent_company">
            <Select
              popupClassName="custom-select-dropdown"
              placeholder={
                form.getFieldValue("role") === "company"
                  ? "Parent company not needed for Company role"
                  : "Select a parent company (optional)"
              }
              disabled={form.getFieldValue("role") === "company"}
              allowClear
              suffixIcon={<CaretDownOutlined />}
            >
              {companies?.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
